import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {Suspense} from 'react'
import {createSeededRandom} from '@github-ui/use-stable-random'
import {ForYouItemSkeleton} from './shared/ForYouItemFallbacks'
import styles from './shared/ForYouSubSection.module.css'
import {useDecideNextAction} from './deciders/use-decide-next-action'
import {useViewerRoles} from './shared/use-viewer-roles'
import {useReviewRequestedData} from './reviewer/use-review-requested-data'
import {clsx} from 'clsx'
import {useLayoutQuery} from '../../../page-data/loaders/use-layout-query'
import {useDownstackPendingReviewRequests} from './stack/use-downstack-pending-review-request'
import {useMergeabilityData} from './mergeability/use-mergeability-data'
import {useLayoutPullRequestId} from '../../../stores/layout-selectors'

export function ForYouNextAction() {
  const pullRequestId = useLayoutPullRequestId()
  // React IDs can change on remount; the same pull request should keep its loading phrase.
  const phraseIndex = createSeededRandom('ForYouNextAction')(pullRequestId, LOADING_PHRASES.length)
  const loadingFallback = (
    <section aria-label="Recommended action">
      <ForYouNextActionSkeleton text={LOADING_PHRASES[phraseIndex]} />
    </section>
  )

  return (
    <ErrorBoundary boundaryName="ForYouNextActionBoundary" fallback={null}>
      <Suspense fallback={loadingFallback}>
        <ForYouNextActionContent loadingFallback={loadingFallback} />
      </Suspense>
    </ErrorBoundary>
  )
}

function ForYouNextActionContent({loadingFallback}: {loadingFallback: React.ReactNode}) {
  const {queryStatus: viewerRolesQueryStatus} = useViewerRoles()
  const {queryStatus: reviewRequestsQueryStatus} = useReviewRequestedData()
  const {pullRequest, stack} = useLayoutQuery().data
  const {queryStatus: downstackPendingReviewRequestQueryStatus} = useDownstackPendingReviewRequests(
    stack,
    pullRequest.number,
  )
  const mergeabilityData = useMergeabilityData()
  const recommendedAction = useDecideNextAction()
  if (
    viewerRolesQueryStatus === 'loading' ||
    reviewRequestsQueryStatus === 'loading' ||
    downstackPendingReviewRequestQueryStatus === 'loading' ||
    (mergeabilityData?.stackSection.loadingEntryCount ?? 0) > 0
  ) {
    return loadingFallback
  }

  if (viewerRolesQueryStatus !== 'success' || reviewRequestsQueryStatus !== 'success' || recommendedAction === null) {
    return null
  }

  // since we're re-using the For You implementation, we need to wrap the list of items in a <ul>
  // so that the <li> elements inside ForYouItem are valid for a11y.
  return (
    <section aria-label="Recommended action">
      {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
      <ul role="list" className={clsx(styles.list, styles.CondensedForYouList)}>
        {recommendedAction}
      </ul>
    </section>
  )
}

function ForYouNextActionSkeleton({text}: {text: string | undefined}) {
  return (
    <ul className={clsx(styles.list, styles.CondensedForYouList)}>
      <ForYouItemSkeleton text={text} actionWidth="120px" />
    </ul>
  )
}

const LOADING_PHRASES = [
  "Deserializing your pull request's destiny…",
  'git blame-ing the delay…',
  'Traversing the commit graph…',
  'Reticulating CI pipelines…',
  'Garbage collecting stale opinions…',
  'Checking if the dog ate your approvals…',
  'Begging the automerge daemon for a straight answer…',
  'Asking git log --oneline for the meaning of life…',
  'Checking out your fate on a detached HEAD…',
]
